import { createContext, useContext, type ReactNode } from 'react';
import {
  useServerHealth,
  type UseServerHealthReturn,
  type UseServerHealthOptions,
} from '@/hooks/useServerHealth';
import { useNetwork } from '@/components/network/NetworkProvider';
import { MaintenancePage } from '@/components/common/MaintenancePage';

/**
 * ============================================
 * Server Status Context
 * ============================================
 *
 * Globally manages server availability status.
 *
 * Features:
 * 1. Check server health on app initialization
 * 2. Show maintenance page when unavailable (blocks rendering)
 * 3. Auto-retry + manual retry
 * 4. Provide context for child components
 */

const ServerStatusContext = createContext<UseServerHealthReturn | null>(null);

/**
 * Hook for accessing server status
 */
export function useServerStatus(): UseServerHealthReturn {
  const context = useContext(ServerStatusContext);
  if (!context) {
    throw new Error('useServerStatus must be used within ServerStatusProvider');
  }
  return context;
}

export interface ServerStatusProviderProps extends UseServerHealthOptions {
  children: ReactNode;
  /**
   * Whether to show loading during check
   * Default true, shows loading state
   * Set to false to render children directly during check
   */
  showLoadingOnCheck?: boolean;
  /**
   * Custom maintenance page title
   */
  maintenanceTitle?: string;
  /**
   * Custom maintenance page description
   */
  maintenanceDescription?: string;
}

export function ServerStatusProvider({
  children,
  showLoadingOnCheck = true,
  maintenanceTitle,
  maintenanceDescription,
  ...healthOptions
}: ServerStatusProviderProps) {
  const serverHealth = useServerHealth(healthOptions);
  const { isOnline } = useNetwork();
  const {
    status,
    isChecking,
    isInitialChecking,
    isUnavailable,
    retryIn,
    retry,
  } = serverHealth;

  // Initial check (with retry): show friendly loading state
  // This phase auto-retries quickly, should not show maintenance page
  if (isInitialChecking && showLoadingOnCheck) {
    return (
      <MaintenancePage
        type="maintenance"
        title="Connecting to server"
        description="Please wait..."
        isRetrying={true}
      />
    );
  }

  // Manual retry (non-initial): show checking state
  if (isChecking && !isInitialChecking && showLoadingOnCheck) {
    return (
      <MaintenancePage
        type="maintenance"
        title="Reconnecting"
        description="Please wait..."
        isRetrying={true}
      />
    );
  }

  // Confirmed unavailable (after initial check failures): distinguish offline vs service unavailable
  if (isUnavailable && !isInitialChecking) {
    // Browser reports offline: show network issue page
    if (!isOnline) {
      return (
        <MaintenancePage
          type="network"
          title="Network Connection Issue"
          description="Please check your network connection and try again"
          retryIn={retryIn}
          isRetrying={status === 'checking'}
          onRetry={retry}
        />
      );
    }

    // Online but service unavailable: show maintenance page
    return (
      <MaintenancePage
        type="maintenance"
        title={maintenanceTitle}
        description={maintenanceDescription}
        retryIn={retryIn}
        isRetrying={status === 'checking'}
        onRetry={retry}
      />
    );
  }

  // Available: render children
  return (
    <ServerStatusContext.Provider value={serverHealth}>
      {children}
    </ServerStatusContext.Provider>
  );
}

export default ServerStatusProvider;

